import { useState, useCallback, useEffect } from 'react'
import { api } from '../lib/api'
import { FacilitatorGuard } from '../components/FacilitatorGuard'
import { ScenarioFacilitatorDashboard } from '../components/scenario/ScenarioPlayer'
import type { Scenario, ScenarioSession, ParticipantResponse } from '../components/scenario/ScenarioPlayer'

const CREW_OPTIONS = [
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'stage', label: 'Stage' },
  { value: 'it', label: 'IT' },
]

const TEAM_OPTIONS = [
  { value: 'installation', label: 'Installation' },
  { value: 'operations', label: 'Operations' },
]

interface ApiSession {
  id: string
  crew: string
  team: string
  status: string
  currentScenarioId: string | null
  scenarioData: unknown | null
  conventionId: string
  facilitatorId: string
}

export default function FacilitatePage() {
  return (
    <FacilitatorGuard>
      <FacilitateContent />
    </FacilitatorGuard>
  )
}

function FacilitateContent() {
  const [session, setSession] = useState<ApiSession | null>(null)
  const [creating, setCreating] = useState(false)
  const [crew, setCrew] = useState('video')
  const [team, setTeam] = useState('operations')
  const [scenarioIndex, setScenarioIndex] = useState(0)
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [loadingScenarios, setLoadingScenarios] = useState(false)
  const [sessionScenario, setSessionScenario] = useState<ScenarioSession>({
    id: '',
    scenarioId: '',
    status: 'waiting',
    responses: [],
  })
  const [error, setError] = useState<string | null>(null)
  const [responses, setResponses] = useState<ParticipantResponse[]>([])

  // Load scenarios when crew/team changes
  useEffect(() => {
    async function loadScenarios() {
      setLoadingScenarios(true)
      try {
        const { scenarios: data } = await api.getScenarios({ crew, team })
        setScenarios(data || [])
      } catch (err) {
        console.error('Failed to load scenarios:', err)
        setScenarios([])
      } finally {
        setLoadingScenarios(false)
      }
    }
    loadScenarios()
  }, [crew, team])

  const currentScenario = scenarios[scenarioIndex] || null

  const handleCreateSession = useCallback(async () => {
    setCreating(true)
    setError(null)
    try {
      const { session: newSession } = await api.createPracticeSession({ crew, team })
      setSession(newSession)
      setSessionScenario({
        id: newSession.id,
        scenarioId: '',
        status: 'waiting',
        responses: [],
      })
      setResponses([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
    } finally {
      setCreating(false)
    }
  }, [crew, team])

  const fetchResponses = useCallback(async (sessionId: string, scenarioId: string) => {
    try {
      const { responses: data } = await api.getPracticeResponses(sessionId, scenarioId)
      setResponses(data || [])
    } catch (err) {
      console.error('Failed to fetch responses:', err)
    }
  }, [])

  const handlePushScenario = useCallback(async () => {
    if (!session || !currentScenario) return
    setError(null)
    try {
      await api.pushScenario(session.id, {
        scenario_id: currentScenario.id,
        scenario_data: currentScenario,
      })
      setSessionScenario({
        id: session.id,
        scenarioId: currentScenario.id,
        status: 'active',
        responses: [],
      })
      setResponses([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to push scenario')
    }
  }, [session, currentScenario])

  const handleReveal = useCallback(() => {
    setSessionScenario((s) => ({ ...s, status: 'revealed' }))
  }, [])

  const handleNext = useCallback(async () => {
    if (!session || scenarios.length === 0) return
    const nextIndex = (scenarioIndex + 1) % scenarios.length
    setScenarioIndex(nextIndex)
    const nextScenario = scenarios[nextIndex]

    setError(null)
    try {
      await api.pushScenario(session.id, {
        scenario_id: nextScenario.id,
        scenario_data: nextScenario,
      })
      setSessionScenario({
        id: session.id,
        scenarioId: nextScenario.id,
        status: 'active',
        responses: [],
      })
      setResponses([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to push scenario')
    }
  }, [session, scenarios, scenarioIndex])

  const handleEndSession = useCallback(async () => {
    if (!session) return
    setError(null)
    try {
      await api.endPracticeSession(session.id)
      setSession((s) => (s ? { ...s, status: 'completed' } : null))
      setSessionScenario((s) => ({ ...s, status: 'closed' }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end session')
    }
  }, [session])

  // Poll for responses when scenario is active
  useEffect(() => {
    if (!session || !sessionScenario.scenarioId || sessionScenario.status !== 'active') return

    const interval = setInterval(() => {
      fetchResponses(session.id, sessionScenario.scenarioId)
    }, 2000)

    return () => clearInterval(interval)
  }, [session, sessionScenario.scenarioId, sessionScenario.status, fetchResponses])

  if (!session) {
    return (
      <div className="facilitate-page">
        <div className="facilitate-header">
          <h1>Facilitator Dashboard</h1>
          <p>Create a new practice session for your crew.</p>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="facilitate-create-form">
          <div className="form-group">
            <label htmlFor="crew">Crew</label>
            <select
              id="crew"
              value={crew}
              onChange={(e) => setCrew(e.target.value)}
              className="form-control"
            >
              {CREW_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="team">Team</label>
            <select
              id="team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="form-control"
            >
              {TEAM_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {loadingScenarios && (
            <p className="text-muted">Loading scenarios…</p>
          )}
          {!loadingScenarios && scenarios.length > 0 && (
            <p className="text-muted">{scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} ready</p>
          )}
          {!loadingScenarios && scenarios.length === 0 && (
            <p className="text-warning">No scenarios found for this crew/team.</p>
          )}

          <button
            type="button"
            className="btn btn-primary"
            disabled={creating || scenarios.length === 0}
            onClick={handleCreateSession}
          >
            {creating ? 'Creating…' : 'Start New Session'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="facilitate-page">
      <div className="facilitate-header">
        <h1>Facilitator Dashboard</h1>
        <div className="facilitate-session-info">
          <span className="session-tag">
            {session.crew} / {session.team}
          </span>
          <span className={`session-status session-status--${session.status}`}>
            {session.status}
          </span>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {sessionScenario.status === 'waiting' ? (
        <div className="facilitate-scenario">
          <div className="scenario-waiting">
            <h2>Ready to Start</h2>
            <p>Push the first scenario when your trainees are ready.</p>
            {currentScenario && (
              <div className="scenario-preview">
                <span className="scenario-eyebrow">First scenario</span>
                <h3>{currentScenario.title}</h3>
                <p>{currentScenario.practicePrompt.slice(0, 120)}…</p>
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePushScenario}
              disabled={!currentScenario}
            >
              Push First Scenario
            </button>
          </div>
        </div>
      ) : currentScenario ? (
        <div className="facilitate-scenario">
          <ScenarioFacilitatorDashboard
            scenario={currentScenario}
            session={{
              ...sessionScenario,
              responses,
            }}
            onReveal={handleReveal}
            onNext={handleNext}
          />
        </div>
      ) : (
        <div className="facilitate-scenario">
          <div className="scenario-waiting">
            <h2>No More Scenarios</h2>
            <p>All scenarios have been completed.</p>
          </div>
        </div>
      )}

      <div className="facilitate-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleEndSession}
          disabled={session.status === 'completed'}
        >
          End Session
        </button>
      </div>

      <div className="facilitate-share">
        <p>
          <strong>Share this link with trainees:</strong>
          <br />
          <code>{`${window.location.origin}/practice/${session.id}`}</code>
        </p>
      </div>
    </div>
  )
}
